(() => {
  'use strict';
  const root = document.getElementById('LoshenEducationTools');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';
  const copy = JSON.parse(root.querySelector('[data-lc-education-labels]').textContent);
  const fr = document.documentElement.lang.startsWith('fr');
  const language = fr ? 'fr' : 'en';
  const t = key => copy[key] || '';
  const escape = text => String(text ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const normalize = text => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\([^)]*\)|\[[^\]]*\]/g, ' ').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const plain = html => {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    doc.querySelectorAll('script,style,iframe').forEach(el => el.remove());
    doc.querySelectorAll('p,br,li').forEach(el => el.append('\n'));
    return doc.body.textContent.trim();
  };
  const translated = value => typeof value === 'string' ? value : value?.[language] || value?.en || '';
  let previousFocus;
  function dialog(className, title) {
    const node = document.createElement('dialog');
    node.className = 'lc-education-dialog ' + className;
    node.setAttribute('aria-label', title);
    node.innerHTML = `<header><p>${escape(title)}</p><button type="button" class="lc-education-close" aria-label="${escape(t('close'))}">×</button></header><div class="lc-education-content"></div>`;
    node.querySelector('button').addEventListener('click', () => node.close());
    node.addEventListener('click', event => {
      if (event.target !== node) return;
      const box = node.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) node.close();
    });
    node.addEventListener('close', () => {
      document.body.classList.remove('lc-education-modal-open');
      if (previousFocus?.isConnected) previousFocus.focus({preventScroll:true});
    });
    root.append(node);
    return node;
  }
  function open(node, trigger) {
    previousFocus = trigger || document.activeElement;
    document.body.classList.add('lc-education-modal-open');
    if (!node.open) node.showModal();
  }
  async function json(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 18000);
    try {
      const response = await fetch(url, {credentials:'omit',headers:{Accept:'application/json'},signal:controller.signal});
      if (!response.ok) throw Error('Response ' + response.status);
      return await response.json();
    } finally { clearTimeout(timer); }
  }
  let guidePromise, guideIndex;
  async function guide() {
    if (!guidePromise) {
      guidePromise = json(root.dataset.guideUrl).then(data => {
        const index = new Map();
        const rich = new Map(data.rich.map(entry => [entry.id, entry]));
        for (const row of data.rows) {
          const record = {id:row[0],inci:row[1],name:{en:row[2],fr:row[3]},functions:row[4],sourceIds:row[5],rich:rich.get(row[0])};
          for (const name of [row[1], ...row[6]]) {
            const key = normalize(name);
            if (key && !index.has(key)) index.set(key,record);
          }
        }
        const water = index.get('aqua');
        if (water) ['water aqua eau','aqua water eau','water aqua','aqua water','water','eau'].forEach(key => index.set(key,water));
        guideIndex = {index,guide:data.guide,priority:data.priority};
        return guideIndex;
      }).catch(error => { guidePromise = null; throw error; });
    }
    return guidePromise;
  }
  let ingredientDialog;
  async function showIngredient(name, trigger) {
    ingredientDialog ||= dialog('lc-ingredient-dialog','La Matière');
    const content = ingredientDialog.querySelector('.lc-education-content');
    content.innerHTML = `<h2>${escape(name)}</h2><p role="status">${escape(t('loading'))}</p>`;
    open(ingredientDialog,trigger);
    try {
      const data = await guide();
      const entry = data.index.get(normalize(name));
      if (!entry) {
        content.innerHTML = `<h2>${escape(name)}</h2><p>${escape(t('whole_formula'))}</p><a class="lc-education-link" href="${escape(root.dataset.glossaryUrl)}" target="_blank" rel="noopener">${escape(t('guide_link'))}</a>`;
        return;
      }
      const details = entry.rich;
      const roles = [...entry.functions].sort((a,b) => {
        const rank = key => {const n=data.priority.indexOf(key);return n<0?999:n;};
        return rank(a)-rank(b);
      }).slice(0,3).map(key => data.guide[key]).filter(Boolean);
      const role = details ? translated(details.role) : [...new Set(roles.map(r => translated(r.name)))].join(' · ');
      const definition = details ? translated(details.definition) : '';
      const does = details ? translated(details.does) : roles.map(r => translated(r.description)).join(' ');
      const context = details ? translated(details.context) : t('whole_formula');
      const sources = details?.sources?.length ? details.sources : entry.sourceIds.map(id => ['European Commission · CosIng','https://ec.europa.eu/growth/tools-databases/cosing/details/'+id]);
      const glossary = new URL(root.dataset.glossaryUrl,location.origin);
      glossary.searchParams.set('q',entry.inci);
      content.innerHTML = `<h2>${escape(translated(details?.name || entry.name))}</h2><p class="lc-ingredient-inci">${escape(entry.inci)}</p><p class="lc-ingredient-role">${escape(role)}</p>${definition?`<p>${escape(definition)}</p>`:''}<section class="lc-ingredient-section"><h3>${escape(t('role'))}</h3><p>${escape(does)}</p></section><section class="lc-ingredient-context"><h3>${escape(t('context'))}</h3><p>${escape(context)}</p></section>${sources.length?`<details class="lc-ingredient-sources"><summary>${escape(t('sources'))}</summary>${sources.map(([label,url])=>`<a href="${escape(url)}" target="_blank" rel="noopener">${escape(label)}</a>`).join('')}</details>`:''}<a class="lc-education-link" href="${escape(glossary.href)}" target="_blank" rel="noopener">${escape(t('guide_link'))}</a>`;
    } catch {
      content.innerHTML = `<h2>${escape(name)}</h2><p role="alert">${escape(t('error'))}</p><button class="lc-education-link" data-lc-guide-retry>${escape(t('retry'))}</button>`;
      content.querySelector('button').addEventListener('click',()=>showIngredient(name,trigger));
    }
  }
  function splitIngredients(raw) {
    const parts=[];let depth=0,start=0;
    for(let i=0;i<raw.length;i++) {
      if('(['.includes(raw[i])) depth++;
      if(')]'.includes(raw[i])) depth=Math.max(0,depth-1);
      if((raw[i]===','||raw[i]===';')&&!depth) {
        const numeric=raw[i]===','&&/^\s*\d+(?:\s*,\s*\d+)*\s*$/.test(raw.slice(start,i))&&/^\s*\d+(?:\s*,\s*\d+)*\s*[-–]/.test(raw.slice(i+1));
        if(numeric) continue;
        parts.push(raw.slice(start,i),raw[i]);start=i+1;
      }
    }
    parts.push(raw.slice(start));return parts;
  }
  async function enhanceIngredients(panel) {
    if (!panel || panel.dataset.lcGuideReady || panel.dataset.lcGuideLoading) return;
    panel.dataset.lcGuideLoading='true';
    try {
      const data=await guide();
      let count=0;
      panel.querySelectorAll('p').forEach(paragraph=>{
        const raw=paragraph.textContent;
        if((raw.match(/,/g)||[]).length<3) return;
        const fragment=document.createDocumentFragment();
        for(const part of splitIngredients(raw)) {
          const match=part.match(/^(\s*)([^\n]+?)([.。]?(?:\n[\s\S]*)?)$/);
          const name=match?.[2]?.trim();
          if(!name||!data.index.has(normalize(name))) {fragment.append(part);continue;}
          const button=document.createElement('button');
          button.type='button';button.className='lc-ingredient-word';button.textContent=match[2];button.setAttribute('aria-haspopup','dialog');
          button.dataset.lcIngredient=name;
          fragment.append(match[1],button,match[3]);count++;
        }
        paragraph.replaceChildren(fragment);
      });
      if(count) {
        panel.querySelectorAll('.ghp__inci-hint').forEach(el=>el.remove());
        const hint=document.createElement('p');hint.className='lc-ingredient-hint';hint.textContent=t('hint');
        const formula=panel.querySelector('.ghp__full-formula');
        if(formula) formula.querySelector('summary')?.after(hint);else panel.prepend(hint);
      }
      panel.dataset.lcGuideReady='true';
    } catch { panel.dataset.lcGuideReady=''; }
    finally {delete panel.dataset.lcGuideLoading;}
  }
  document.addEventListener('click',event=>{
    const word=event.target.closest('[data-lc-ingredient],.ghp__inci-highlight');
    if(word) {event.preventDefault();event.stopImmediatePropagation();showIngredient(word.dataset.lcIngredient||word.textContent,word);return;}
    const tab=event.target.closest('[data-ghp-info-tab="ingredients"]');
    if(tab) enhanceIngredients(document.getElementById(tab.getAttribute('aria-controls')));
  },true);
  const information=document.querySelector('[data-ghp-information]');
  if(information) {
    const observe=()=>information.querySelectorAll('[id$="-info-panel-ingredients"]').forEach(panel=>{if(!panel.hidden)enhanceIngredients(panel);});
    new MutationObserver(observe).observe(information,{subtree:true,attributes:true,attributeFilter:['hidden'],childList:true});
    observe();
  }
  const topics={
    texture:/\b(texture|consistency|consistance|thick|thin|lightweight|silky|gel|cream|creme|lather|foam|mousse|fluide|riche|leger|onctueux)\b/i,
    scent:/\b(smell\w*|scent\w*|fragrance|parfum\w*|odeur\w*)\b/i,
    application:/\b(apply|appl\w*|use[ds]?|using|utilis\w*|layer\w*|matin|soir|morning|evening|routine|spread\w*|etale\w*)\b/i,
    packaging:/\b(pump\w*|bottle\w*|packag\w*|cap|tube|flacon\w*|pompe\w*|emball\w*|format)\b/i
  };
  let reviewsPromise,reviewDialog,activeTopic='all',query='',reviews=[],visibleLimit=12;
  const reviewText=review=>normalize(review.title+' '+plain(review.body_html));
  async function loadReviews() {
    if(!reviewsPromise) {
      reviewsPromise=(async()=>{
        const url=new URL('https://api.judge.me/reviews/reviews_for_widget');
        url.search=new URLSearchParams({shop_domain:root.dataset.shopDomain,platform:'shopify',product_id:root.dataset.productId,page:'1',per_page:'30',sort_by:'created_at',sort_dir:'desc'}).toString();
        const first=await json(url.href);
        const pages=Number(first.pagination?.total_pages||1);
        if(!Array.isArray(first.reviews)||pages>200) throw Error('Invalid review data');
        const all=[...first.reviews];
        for(let page=2;page<=pages;page++) {
          url.searchParams.set('page',String(page));
          const next=await json(url.href);
          if(!Array.isArray(next.reviews)) throw Error('Incomplete reviews');
          all.push(...next.reviews);
        }
        const unique=[...new Map(all.map(review=>[review.uuid,review])).values()];
        if(unique.length<Number(first.number_of_reviews||0)) throw Error('Incomplete reviews');
        return unique;
      })().catch(error=>{reviewsPromise=null;throw error;});
    }
    return reviewsPromise;
  }
  function renderReviews() {
    const container=reviewDialog.querySelector('[data-lc-review-results]');
    const matches=reviews.filter(review=>{
      const text=reviewText(review);
      return(activeTopic==='all'||topics[activeTopic].test(text))&&(!query||text.includes(normalize(query)));
    });
    reviewDialog.querySelector('[data-lc-review-count]').textContent=t('matches').replace('{count}',String(matches.length)).replace('{total}',String(reviews.length));
    reviewDialog.querySelectorAll('[data-lc-review-topic]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.lcReviewTopic===activeTopic)));
    container.innerHTML=matches.length?matches.slice(0,visibleLimit).map(review=>{
      let date='';try{date=new Intl.DateTimeFormat(fr?'fr-CA':'en-CA',{dateStyle:'medium'}).format(new Date(review.created_at));}catch{}
      const badges=Array.isArray(review.transparency_badges)?review.transparency_badges:[];
      const disclosure=[badges.includes('review_earned_for_future_purchase')?t('reward'):'',badges.includes('review_collected_from_store_visitor')?t('visitor'):''].filter(Boolean).join(' · ');
      const rating=Math.max(1,Math.min(5,Number(review.rating)||1));
      return `<article class="lc-topic-review"><span class="lc-review-stars" role="img" aria-label="${rating}/5">${'★'.repeat(rating)}${'☆'.repeat(5-rating)}</span><h3>${escape(review.title)}</h3><p class="lc-education-meta">${escape(review.reviewer_name||review.reviewer_initial||'')} ${review.verified_buyer?'· '+escape(t('verified')):''} · ${escape(date)}</p><blockquote>${escape(plain(review.body_html))}</blockquote>${disclosure?`<p class="lc-review-disclosure">${escape(disclosure)}</p>`:''}${review.is_for_product_from_group?`<p class="lc-review-disclosure">${escape(review.product_title)}</p>`:''}${review.reply_content?`<div class="lc-review-reply">${escape(t('reply'))}\n${escape(plain(review.reply_content))}</div>`:''}</article>`;
    }).join(''):`<p>${escape(t('none'))}</p>`;
    const more=reviewDialog.querySelector('[data-lc-review-more]');more.hidden=matches.length<=visibleLimit;
  }
  async function showReviews(topic,trigger) {
    reviewDialog ||= dialog('lc-review-explorer',t('reviews'));
    activeTopic=topic;query='';visibleLimit=12;
    const content=reviewDialog.querySelector('.lc-education-content');
    content.innerHTML=`<h2>${escape(t('curious'))}</h2><p>${escape(root.dataset.productTitle)}</p><div class="lc-education-pills">${['all',...Object.keys(topics)].map(key=>`<button type="button" data-lc-review-topic="${key}" aria-pressed="${key===topic}">${escape(t(key))}</button>`).join('')}</div><div class="lc-review-search"><label for="LcReviewTopicSearch">${escape(t('search'))}</label><input id="LcReviewTopicSearch" type="search" placeholder="${escape(t('search_placeholder'))}" autocomplete="off"></div><p class="lc-review-match-count" data-lc-review-count role="status">${escape(t('loading'))}</p><div data-lc-review-results></div><button type="button" class="lc-education-link" data-lc-review-more hidden>${escape(t('more'))}</button><p class="lc-education-meta">${escape(t('review_note'))}</p>`;
    content.querySelector('input').addEventListener('input',event=>{query=event.target.value;visibleLimit=12;renderReviews();});
    content.querySelectorAll('[data-lc-review-topic]').forEach(button=>button.addEventListener('click',()=>{activeTopic=button.dataset.lcReviewTopic;visibleLimit=12;renderReviews();}));
    content.querySelector('[data-lc-review-more]').addEventListener('click',()=>{visibleLimit+=12;renderReviews();});
    open(reviewDialog,trigger);
    try {reviews=await loadReviews();renderReviews();}
    catch {
      content.querySelector('[data-lc-review-count]').textContent=t('error');
      content.querySelector('[data-lc-review-results]').innerHTML=`<button type="button" class="lc-education-link" data-lc-review-retry>${escape(t('retry'))}</button>`;
      content.querySelector('[data-lc-review-retry]').addEventListener('click',()=>showReviews(activeTopic,trigger));
    }
  }
  function mountReviews() {
    const widget=document.querySelector('#judgeme_product_reviews');
    if(!widget||document.querySelector('.lc-review-topics')) return !!widget;
    const template=root.querySelector('[data-lc-review-topics-template]');
    if(!template?.content.firstElementChild) return true;
    const section=template.content.firstElementChild.cloneNode(true);
    section.querySelectorAll('button').forEach(button=>button.addEventListener('click',()=>showReviews(button.dataset.lcReviewTopic,button)));
    widget.before(section);return true;
  }
  if(!mountReviews()) {
    const observer=new MutationObserver(()=>{if(mountReviews())observer.disconnect();});
    observer.observe(document.getElementById('main'),{childList:true,subtree:true});
    setTimeout(()=>observer.disconnect(),15000);
  }
  const alternativeTemplate=root.querySelector('[data-lc-alternatives-template]');
  if(alternativeTemplate) {
    const section=alternativeTemplate.content.firstElementChild.cloneNode(true);
    const formPrefix='product-form-main-'+root.dataset.productId+'-';
    const variants=JSON.parse(root.querySelector('[data-lc-alternative-variants]').textContent);
    const assets=root.querySelector('[data-lc-alternative-assets]');
    let available=section.dataset.initialAvailable==='true';
    let frame=0,comparisonPromise,contentMounted=false;
    const place=()=>{
      frame=0;
      if(available){section.open=false;section.remove();return;}
      const purchase=document.querySelector('buy-buttons[form^="'+formPrefix+'"]');
      if(!purchase) return;
      if(purchase.nextElementSibling!==section)purchase.after(section);
    };
    const schedule=()=>{if(!frame)frame=requestAnimationFrame(place);};
    place();
    document.addEventListener('variant:change',event=>{
      const eventFormId=event.target?.getAttribute?.('id');
      if(typeof eventFormId!=='string'||!eventFormId.startsWith(formPrefix))return;
      const variant=event.detail.variant;
      available=!variant||!Object.hasOwn(variants,String(variant.id))||variant.available!==false;
      if(variant)section.dataset.sourceUrl=new URL(section.dataset.sourceUrl,location.origin).pathname+'?variant='+variant.id;
      schedule();
    });
    const purchase=document.querySelector('buy-buttons[form^="'+formPrefix+'"]');
    const main=purchase?.closest('product-rerender');
    if(main)new MutationObserver(schedule).observe(main,{childList:true,subtree:true});
    section.addEventListener('toggle',()=>{
      if(!section.open||contentMounted)return;
      section.append(section.querySelector('[data-lc-alternative-content]').content.cloneNode(true));
      contentMounted=true;
    });
    const loadComparison=()=>{
      if(window.LoshenCompare?.compareProducts)return Promise.resolve(window.LoshenCompare.compareProducts);
      if(comparisonPromise)return comparisonPromise;
      comparisonPromise=new Promise((resolve,reject)=>{
        const style=document.createElement('link');style.rel='stylesheet';style.href=assets.dataset.style;
        const script=document.createElement('script');script.src=assets.dataset.script;script.async=true;
        script.onload=()=>{
          if(window.LoshenCompare?.compareProducts)resolve(window.LoshenCompare.compareProducts);
          else {script.remove();style.remove();comparisonPromise=null;reject(new Error('Comparison unavailable'));}
        };
        script.onerror=()=>{script.remove();style.remove();comparisonPromise=null;reject(new Error('Comparison unavailable'));};
        document.head.append(style,script);
      });
      return comparisonPromise;
    };
    section.addEventListener('click',async event=>{
      const button=event.target.closest('[data-lc-compare-option]');
      if(!button||!section.contains(button))return;
      const status=section.querySelector('[data-lc-comparison-status]');
      button.disabled=true;status.textContent=t('comparison_loading');
      try {
        const compare=await loadComparison();
        status.textContent='';
        compare([{id:section.dataset.sourceId,title:section.dataset.sourceTitle,url:section.dataset.sourceUrl,image:section.dataset.sourceImage},{id:button.dataset.optionId,title:button.dataset.optionTitle,url:button.dataset.optionUrl,image:button.dataset.optionImage}]);
      } catch {status.textContent=t('error');}
      finally {button.disabled=false;}
    });
  }
})();


